import { useState, useEffect, useCallback } from "react";
import { Plus, BookMarked, Download, X, Sun, Moon, Zap, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { dbGet, dbSet } from "../lib/db";
import DayEditor from "./DayEditor";
import RelapsePlan from "./RelapsePlan";
import NotifSettings, { scheduleReminders } from "./NotifSettings";
import WeeklySummary from "./WeeklySummary";

// ─── Constants ────────────────────────────────────────────────────────────────

const MOODS = ["😔", "😕", "😐", "🙂", "😊", "😄"];

const TRIGGERS = [
  "Estrés", "Soledad", "Aburrimiento", "Ansiedad", "Tristeza",
  "Conflicto familiar", "Trabajo", "Problemas económicos",
  "Celebración", "Dolor físico", "Insomnio", "Recuerdos",
];

const MOMENT_TYPES = [
  { id: "ansioso",    emoji: "😤", label: "Ansioso" },
  { id: "bien",       emoji: "😊", label: "Me sentí bien" },
  { id: "craving",   emoji: "😰", label: "Craving" },
  { id: "supere",    emoji: "💪", label: "Lo superé" },
  { id: "bajon",     emoji: "😔", label: "Bajón" },
  { id: "euforia",   emoji: "⚡", label: "Euforia" },
  { id: "enojo",     emoji: "😠", label: "Enojo" },
  { id: "calma",     emoji: "😌", label: "Momento de calma" },
  { id: "ayuda",     emoji: "🤝", label: "Pedí ayuda" },
  { id: "medicacion",emoji: "💊", label: "Medicación extra" },
];

const WAKE_STATES = [
  { id: "bien",    label: "Bien" },
  { id: "regular", label: "Regular" },
  { id: "mal",     label: "Mal" },
];

const SK  = "reconectados_journal";
const MK  = "reconectados_morning";
const MOK = "reconectados_moments";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayStr = () => new Date().toDateString();
const dateKey  = () => new Date().toISOString().slice(0, 10);

// loadJSON/saveJSON aliased to IndexedDB for backwards compat within this file
const loadJSON = (key, fallback) => dbGet(key, fallback);
const saveJSON  = (key, val)     => dbSet(key, val);

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("es-ES", {
    weekday: "short", day: "numeric", month: "short",
  });
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

function riskColor(v) {
  if (v <= 3) return "#84A98C";
  if (v <= 6) return "#F4A261";
  return "#E07A5F";
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

// ─── Calendar ─────────────────────────────────────────────────────────────────

const WEEKDAYS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function buildCalendarDots(eveningEntries, morningMap, momentsAll) {
  const dots = {};

  eveningEntries.forEach(e => {
    const k = e.date.slice(0, 10);
    if (!dots[k]) dots[k] = new Set();
    dots[k].add("evening");
  });

  Object.keys(morningMap).forEach(k => {
    if (!dots[k]) dots[k] = new Set();
    dots[k].add("morning");
  });

  momentsAll.forEach(m => {
    const k = m.time.slice(0, 10);
    if (!dots[k]) dots[k] = new Set();
    dots[k].add("moment");
  });

  return dots;
}

function JournalCalendar({ eveningEntries, morningMap, momentsAll, onSelectDay }) {
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selected, setSelected] = useState(isoDate(today));

  const dots = buildCalendarDots(eveningEntries, morningMap, momentsAll);

  const firstDay = new Date(year, month, 1);
  // Monday-based week: getDay() returns 0=Sun → map to 6, 1=Mon→0, …
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  const handleDay = (day) => {
    const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setSelected(key);
    onSelectDay(key);
  };

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] p-4 mb-4">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-[#F7F9F7] transition-colors">
          <ChevronLeft size={16} className="text-[#2C4238]" />
        </button>
        <p className="text-sm font-semibold text-[#1E2F23] capitalize" style={{ fontFamily: "Manrope" }}>
          {MONTHS_ES[month]} {year}
        </p>
        <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-[#F7F9F7] transition-colors">
          <ChevronRight size={16} className="text-[#2C4238]" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-[#4A6E5C] py-1">{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const todayKey = isoDate(today);
          const isToday = key === todayKey;
          const isSelected = key === selected;
          const dayDots = dots[key];

          return (
            <button
              key={key}
              onClick={() => handleDay(day)}
              className={`relative flex flex-col items-center justify-center py-1.5 rounded-xl transition-all ${
                isSelected
                  ? "bg-[#6B9080] text-white"
                  : isToday
                  ? "bg-[#EEF4F1] text-[#6B9080]"
                  : "text-[#1E2F23] hover:bg-[#F7F9F7]"
              }`}
            >
              <span className={`text-xs font-medium leading-none ${isSelected ? "text-white" : isToday ? "font-bold" : ""}`}>{day}</span>
              {dayDots && (
                <div className="flex gap-0.5 mt-0.5">
                  {dayDots.has("morning") && (
                    <div className={`w-1 h-1 rounded-full ${isSelected ? "bg-white/80" : "bg-[#F4A261]"}`} />
                  )}
                  {dayDots.has("moment") && (
                    <div className={`w-1 h-1 rounded-full ${isSelected ? "bg-white/80" : "bg-[#5B8FCC]"}`} />
                  )}
                  {dayDots.has("evening") && (
                    <div className={`w-1 h-1 rounded-full ${isSelected ? "bg-white/80" : "bg-[#6B9080]"}`} />
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#F0F5F2]">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[#F4A261]" />
          <span className="text-xs text-[#2C4238]">Mañana</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[#5B8FCC]" />
          <span className="text-xs text-[#2C4238]">Momentos</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[#6B9080]" />
          <span className="text-xs text-[#2C4238]">Noche</span>
        </div>
      </div>
    </div>
  );
}

// ─── Day Detail ───────────────────────────────────────────────────────────────

function DayDetail({ dayKey, eveningEntries, morningMap, momentsAll }) {
  const evening = eveningEntries.find(e => e.date.slice(0, 10) === dayKey);
  const morning = morningMap[dayKey];
  const dayMoments = momentsAll.filter(m => m.time.slice(0, 10) === dayKey);

  const hasAny = evening || morning || dayMoments.length > 0;

  const dateLabel = new Date(dayKey + "T12:00:00").toLocaleDateString("es-ES", {
    weekday: "long", day: "numeric", month: "long",
  });

  if (!hasAny) {
    return (
      <div className="bg-white rounded-2xl border border-[#E4EBE6] p-4 mb-4 animate-fade-in">
        <p className="text-xs font-semibold text-[#2C4238] capitalize mb-1">{dateLabel}</p>
        <p className="text-sm text-[#4A6E5C]">Sin registros para este día.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] p-4 mb-4 animate-fade-in space-y-4">
      <p className="text-xs font-semibold text-[#2C4238] capitalize">{dateLabel}</p>

      {morning && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-[#FFF9EE] flex items-center justify-center">
              <Sun size={13} className="text-[#F4A261]" />
            </div>
            <p className="text-xs font-semibold text-[#1E2F23]">Check-in matutino</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-[#2C4238]">
            <span className="px-2 py-1 bg-[#F7F9F7] rounded-lg border border-[#E4EBE6]">
              {WAKE_STATES.find(w => w.id === morning.wakeState)?.label}
            </span>
            <span className="px-2 py-1 bg-[#F7F9F7] rounded-lg border border-[#E4EBE6]">
              {morning.sueno}h sueño
            </span>
            <span className="px-2 py-1 bg-[#F7F9F7] rounded-lg border border-[#E4EBE6]">
              Impulso {morning.impulso}/5
            </span>
            {morning.medicacion && (
              <span className="px-2 py-1 bg-[#EEF4F1] text-[#6B9080] rounded-lg border border-[#C8DDD5]">
                Medicación ✓
              </span>
            )}
          </div>
        </div>
      )}

      {dayMoments.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-[#EDF4FF] flex items-center justify-center">
              <Zap size={13} className="text-[#5B8FCC]" />
            </div>
            <p className="text-xs font-semibold text-[#1E2F23]">Momentos del día</p>
          </div>
          <div className="space-y-1.5">
            {dayMoments.map((m, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-base leading-none mt-0.5">{m.emoji}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-[#1E2F23]">{m.label}</p>
                    <p className="text-xs text-[#4A6E5C]">{formatTime(m.time)}</p>
                  </div>
                  {m.note && <p className="text-xs text-[#2C4238] mt-0.5">{m.note}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {evening && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-[#EEF4F1] flex items-center justify-center">
              <Moon size={13} className="text-[#6B9080]" />
            </div>
            <p className="text-xs font-semibold text-[#1E2F23]">Check-in nocturno</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs mb-2">
            <span className="text-xl">{MOODS[evening.mood] ?? "😐"}</span>
            <span className="px-2 py-1 bg-[#F7F9F7] rounded-lg border border-[#E4EBE6] text-[#2C4238]">
              Riesgo <span style={{ color: riskColor(evening.riskLevel) }} className="font-bold">{evening.riskLevel}/10</span>
            </span>
            {evening.ansiedad && (
              <span className="px-2 py-1 bg-[#F7F9F7] rounded-lg border border-[#E4EBE6] text-[#5B8FCC]">
                Ansiedad {evening.ansiedad}/5
              </span>
            )}
            {evening.consumo && (
              <span className="px-2 py-1 bg-[#FFF4F2] rounded-lg border border-[#F4C5B5] text-[#E07A5F] font-medium">
                Consumo
              </span>
            )}
          </div>
          {evening.triggers?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {evening.triggers.map(t => (
                <span key={t} className="px-2 py-0.5 bg-[#F7F9F7] text-[#2C4238] rounded-full text-xs border border-[#E4EBE6]">{t}</span>
              ))}
            </div>
          )}
          {evening.notes && (
            <p className="text-xs text-[#2C4238] leading-relaxed italic">"{evening.notes}"</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Morning Check-in ─────────────────────────────────────────────────────────

function MorningCheckIn({ onSave, existing }) {
  const [open, setOpen]             = useState(!existing);
  const [wakeState, setWakeState]   = useState(existing?.wakeState ?? "bien");
  const [sueno, setSueno]           = useState(existing?.sueno ?? 7);
  const [medicacion, setMedicacion] = useState(existing?.medicacion ?? false);
  const [impulso, setImpulso]       = useState(existing?.impulso ?? 1);

  const save = async () => {
    const data = { wakeState, sueno, medicacion, impulso, savedAt: new Date().toISOString() };
    const all  = await loadJSON(MK, {});
    all[dateKey()] = data;
    await saveJSON(MK, all);
    onSave(data);
    setOpen(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] mb-4 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#FFF9EE] flex items-center justify-center">
            <Sun size={18} className="text-[#F4A261]" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-sm text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>
              Check-in matutino
            </p>
            {existing ? (
              <p className="text-xs text-[#84A98C]">
                Completado · {WAKE_STATES.find(w => w.id === existing.wakeState)?.label} · {existing.sueno}h sueño
              </p>
            ) : (
              <p className="text-xs text-[#F4A261]">Pendiente de hoy</p>
            )}
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-[#4A6E5C]" /> : <ChevronDown size={16} className="text-[#4A6E5C]" />}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-[#F0F5F2] animate-fade-in">
          {/* Wake state */}
          <div className="mt-4 mb-4">
            <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-2">¿Cómo amaneciste?</p>
            <div className="flex gap-2">
              {WAKE_STATES.map(w => (
                <button
                  key={w.id}
                  onClick={() => setWakeState(w.id)}
                  className={`flex-1 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${
                    wakeState === w.id
                      ? "bg-[#6B9080] text-white border-[#6B9080]"
                      : "bg-white text-[#2C4238] border-[#E4EBE6]"
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sleep */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide">Horas de sueño</p>
              <span className="text-base font-bold text-[#1E2F23]">{sueno}h</span>
            </div>
            <input
              type="range" min="2" max="12" step="0.5"
              value={sueno}
              onChange={e => setSueno(parseFloat(e.target.value))}
              className="w-full accent-[#6B9080]"
            />
          </div>

          {/* Medication */}
          <div className="mb-4 flex items-center justify-between bg-[#F7F9F7] rounded-xl px-4 py-3">
            <p className="text-xs font-semibold text-[#2C4238]">¿Tomé mi medicación?</p>
            <button
              onClick={() => setMedicacion(m => !m)}
              className={`w-12 h-6 rounded-full transition-colors relative ${medicacion ? "bg-[#6B9080]" : "bg-[#E4EBE6]"}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${medicacion ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </div>

          {/* Impulse */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide">Impulso de consumo (1-5)</p>
              <span className={`text-base font-bold ${impulso <= 2 ? "text-[#84A98C]" : impulso <= 3 ? "text-[#F4A261]" : "text-[#E07A5F]"}`}>{impulso}</span>
            </div>
            <input
              type="range" min="1" max="5"
              value={impulso}
              onChange={e => setImpulso(Number(e.target.value))}
              className="w-full accent-[#6B9080]"
            />
          </div>

          <button
            onClick={save}
            className="w-full py-3 rounded-2xl bg-[#6B9080] text-white font-semibold text-sm"
          >
            Guardar check-in matutino
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Moment Logger ────────────────────────────────────────────────────────────

function MomentLogger({ onSave }) {
  const [open, setOpen]       = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote]       = useState("");

  const save = async () => {
    if (!selected) return;
    const m = { ...selected, note, time: new Date().toISOString() };
    const all = await loadJSON(MOK, []);
    all.push(m);
    await saveJSON(MOK, all);
    onSave(m);
    setSelected(null);
    setNote("");
    setOpen(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] mb-4">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#EDF4FF] flex items-center justify-center">
            <Zap size={18} className="text-[#5B8FCC]" />
          </div>
          <p className="font-semibold text-sm text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>
            Registrar momento
          </p>
        </div>
        <Plus size={18} className="text-[#4A6E5C]" />
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-[#F0F5F2] animate-fade-in">
          <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mt-4 mb-3">¿Qué ocurrió?</p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {MOMENT_TYPES.map(mt => (
              <button
                key={mt.id}
                onClick={() => setSelected(mt)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-xs font-medium transition-all ${
                  selected?.id === mt.id
                    ? "border-[#6B9080] bg-[#EEF4F1] text-[#1E2F23]"
                    : "border-[#E4EBE6] text-[#2C4238]"
                }`}
              >
                <span className="text-base">{mt.emoji}</span> {mt.label}
              </button>
            ))}
          </div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Nota opcional..."
            className="w-full p-3 rounded-xl border border-[#E4EBE6] bg-[#F7F9F7] text-sm text-[#1E2F23] resize-none h-16 focus:outline-none focus:border-[#6B9080] mb-4"
          />
          <div className="flex gap-2">
            <button onClick={() => setOpen(false)} className="flex-1 py-3 rounded-2xl border border-[#E4EBE6] text-[#2C4238] text-sm font-semibold">
              Cancelar
            </button>
            <button
              onClick={save}
              disabled={!selected}
              className="flex-1 py-3 rounded-2xl bg-[#6B9080] text-white text-sm font-semibold disabled:opacity-40"
            >
              Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Evening Check-in Form ────────────────────────────────────────────────────

function EveningForm({ onSave, onClose }) {
  const [mood, setMood]             = useState(3);
  const [riskLevel, setRiskLevel]   = useState(3);
  const [ansiedad, setAnsiedad]     = useState(2);
  const [consumo, setConsumo]       = useState(false);
  const [selectedTriggers, setSelected] = useState([]);
  const [notes, setNotes]           = useState("");

  const toggle = t => setSelected(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);

  const save = async () => {
    const todayDateKey = new Date().toISOString().slice(0, 10);
    const entry = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      mood, riskLevel, ansiedad, consumo,
      triggers: selectedTriggers,
      notes,
    };
    const existing = await loadJSON(SK, []);
    const deduped = existing.filter(e => e.date.slice(0, 10) !== todayDateKey);
    deduped.push(entry);
    await saveJSON(SK, deduped);

    const todayStr2 = new Date().toDateString();
    const lastDate  = await dbGet("reconectados_last_entry", null);
    const streak    = await dbGet("reconectados_streak", 0);
    if (lastDate !== todayStr2) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const newStreak = lastDate === yesterday.toDateString() ? streak + 1 : 1;
      await dbSet("reconectados_streak", newStreak);
      await dbSet("reconectados_last_entry", todayStr2);
    }
    onSave();
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] p-5 mb-4 animate-fade-in-up">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#EEF4F1] flex items-center justify-center">
            <Moon size={18} className="text-[#6B9080]" />
          </div>
          <h3 className="font-bold text-[#1E2F23] text-base" style={{ fontFamily: "Manrope" }}>Check-in nocturno</h3>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#F7F9F7] flex items-center justify-center">
          <X size={16} className="text-[#2C4238]" />
        </button>
      </div>

      {/* Mood */}
      <div className="mb-5">
        <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-3">¿Cómo terminaste el día?</p>
        <div className="flex justify-between">
          {MOODS.map((emoji, i) => (
            <button
              key={i}
              onClick={() => setMood(i)}
              className={`w-11 h-11 rounded-full text-2xl flex items-center justify-center transition-all border-2 ${
                mood === i ? "border-[#6B9080] bg-[#EEF4F1] scale-110" : "border-transparent bg-[#F7F9F7]"
              }`}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>

      {/* Risk */}
      <div className="mb-4">
        <div className="flex justify-between mb-2">
          <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide">Nivel de riesgo</p>
          <span className="text-sm font-bold" style={{ color: riskColor(riskLevel) }}>{riskLevel}/10</span>
        </div>
        <input type="range" min="1" max="10" value={riskLevel} onChange={e => setRiskLevel(Number(e.target.value))} className="w-full accent-[#6B9080]" />
      </div>

      {/* Anxiety */}
      <div className="mb-4">
        <div className="flex justify-between mb-2">
          <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide">Ansiedad</p>
          <span className="text-sm font-bold" style={{ color: riskColor(ansiedad * 2) }}>{ansiedad}/5</span>
        </div>
        <input type="range" min="1" max="5" value={ansiedad} onChange={e => setAnsiedad(Number(e.target.value))} className="w-full accent-[#6B9080]" />
      </div>

      {/* Consumption */}
      <div className="mb-5 flex items-center justify-between bg-[#FFF9F7] rounded-xl px-4 py-3 border border-[#F4C5B5]">
        <p className="text-xs font-semibold text-[#8B5A48]">¿Hubo consumo hoy?</p>
        <button
          onClick={() => setConsumo(c => !c)}
          className={`w-12 h-6 rounded-full transition-colors relative ${consumo ? "bg-[#E07A5F]" : "bg-[#E4EBE6]"}`}
        >
          <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${consumo ? "translate-x-7" : "translate-x-1"}`} />
        </button>
      </div>

      {/* Triggers */}
      <div className="mb-5">
        <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-3">Triggers identificados</p>
        <div className="flex flex-wrap gap-2">
          {TRIGGERS.map(t => (
            <button
              key={t}
              onClick={() => toggle(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                selectedTriggers.includes(t)
                  ? "bg-[#6B9080] text-white border-[#6B9080]"
                  : "bg-white text-[#2C4238] border-[#E4EBE6]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="mb-5">
        <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-2">Notas libres</p>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="¿Qué estrategias usaste? ¿Qué aprendiste hoy?"
          className="w-full p-4 rounded-2xl border border-[#E4EBE6] bg-[#F7F9F7] text-sm text-[#1E2F23] resize-none h-24 focus:outline-none focus:border-[#6B9080]"
        />
      </div>

      <button onClick={save} className="w-full py-4 rounded-2xl bg-[#6B9080] text-white font-semibold text-base">
        Guardar entrada nocturna
      </button>
    </div>
  );
}

// ─── 14-Day Chart ─────────────────────────────────────────────────────────────

function RiskChart({ entries }) {
  const data = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const entry = entries.find(e => e.date.slice(0, 10) === key);
    data.push({
      day: d.toLocaleDateString("es-ES", { day: "numeric", month: "short" }),
      riesgo: entry?.riskLevel ?? null,
      ansiedad: entry?.ansiedad ?? null,
    });
  }

  const hasData = data.some(d => d.riesgo !== null);
  if (!hasData) return null;

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] p-4 mb-4">
      <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-3">Últimos 14 días</p>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={data} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
          <XAxis dataKey="day" tick={{ fontSize: 9, fill: "#A4C3B2" }} interval={3} />
          <YAxis domain={[0, 10]} tick={{ fontSize: 9, fill: "#A4C3B2" }} />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E4EBE6" }}
            formatter={(val, name) => [val ?? "—", name === "riesgo" ? "Riesgo" : "Ansiedad×2"]}
          />
          <ReferenceLine y={7} stroke="#E07A5F" strokeDasharray="3 3" strokeWidth={1} />
          <Line dataKey="riesgo" stroke="#6B9080" strokeWidth={2} dot={{ r: 2, fill: "#6B9080" }} connectNulls={false} />
          <Line dataKey="ansiedad" stroke="#5B8FCC" strokeWidth={1.5} dot={false} connectNulls={false} strokeDasharray="4 2" />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex gap-4 mt-2">
        <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-[#6B9080]" /><span className="text-xs text-[#2C4238]">Riesgo</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-[#5B8FCC]" style={{ borderTop: "2px dashed #5B8FCC", background: "none" }} /><span className="text-xs text-[#2C4238]">Ansiedad</span></div>
      </div>
    </div>
  );
}

// ─── Main Journal ─────────────────────────────────────────────────────────────

const VIEWS = [
  { id: "today",    label: "Hoy" },
  { id: "calendar", label: "Calendario" },
  { id: "history",  label: "Historial" },
  { id: "plan",     label: "Mi Plan" },
];

export default function Journal() {
  const [entries, setEntries]     = useState([]);
  const [momentsAll, setMomentsAll] = useState([]);
  const [morningMap, setMorningMap] = useState({});
  const [morning, setMorning]     = useState(null);
  const [todayMoments, setTodayMoments] = useState([]);
  const [streak, setStreak]       = useState(0);
  const [showEvening, setShowEvening] = useState(false);
  const [hasEveningToday, setHasEveningToday] = useState(false);
  const [view, setView]           = useState("today");
  const [selectedDay, setSelectedDay] = useState(isoDate(new Date()));
  const [editOpen, setEditOpen]   = useState(false);

  const reload = useCallback(async () => {
    const [stored, allMornings, allMoments, streak] = await Promise.all([
      dbGet(SK, []),
      dbGet(MK, {}),
      dbGet(MOK, []),
      dbGet("reconectados_streak", 0),
    ]);

    setEntries([...stored].reverse());
    setHasEveningToday(!!stored.find(e => new Date(e.date).toDateString() === todayStr()));
    setMorningMap(allMornings);
    setMorning(allMornings[dateKey()] ?? null);
    setMomentsAll(allMoments);
    setTodayMoments(allMoments.filter(m => new Date(m.time).toDateString() === todayStr()));
    setStreak(streak);
  }, []);

  useEffect(() => {
    reload();
    // Schedule reminders if configured and permission already granted
    dbGet("reconectados_notifs", null).then(s => {
      if (s && Notification?.permission === "granted") scheduleReminders(s);
    });
  }, [reload]);
  useEffect(() => { setEditOpen(false); }, [selectedDay]);

  const exportTxt = async (numDays) => {
    const [eveningList, morningMap, allMoments] = await Promise.all([
      dbGet(SK, []),
      dbGet(MK, {}),
      dbGet(MOK, []),
    ]);

    const today    = new Date();
    const dayKeys  = [];
    for (let i = numDays - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      dayKeys.push(d.toISOString().slice(0, 10));
    }

    const label   = numDays === 7 ? "semanal" : "mensual";
    const fromLbl = new Date(dayKeys[0] + "T12:00:00").toLocaleDateString("es-ES");
    const toLbl   = new Date(dayKeys[dayKeys.length - 1] + "T12:00:00").toLocaleDateString("es-ES");
    const SEP     = "═".repeat(46);
    const WAKE    = { bien: "Bien", regular: "Regular", mal: "Mal" };
    const EMOJIS  = ["😔", "😕", "😐", "🙂", "😊", "😄"];

    const lines = [
      "RECONECTADOS — Mi Diario de Recuperación",
      `Exportación ${label}: ${fromLbl} – ${toLbl}`,
      SEP, "",
    ];

    let regDays = 0, totalRisk = 0, riskCount = 0;
    const triggerCounts = {};

    for (const day of dayKeys) {
      const morning    = morningMap[day];
      const evening    = eveningList.find(e => e.date?.slice(0, 10) === day);
      const dayMoments = allMoments.filter(m => m.time?.slice(0, 10) === day);
      const hasData    = morning || evening || dayMoments.length > 0;

      const dateLbl = new Date(day + "T12:00:00").toLocaleDateString("es-ES", {
        weekday: "long", day: "numeric", month: "long",
      });
      lines.push(dateLbl.toUpperCase());

      if (!hasData) {
        lines.push("  Sin registros");
      } else {
        regDays++;
        if (morning) {
          const med = morning.medicacion ? " · Medicación ✓" : "";
          lines.push(`  ☀️  Mañana: ${WAKE[morning.wakeState] ?? "—"} · ${morning.sueno}h sueño · Impulso ${morning.impulso}/5${med}`);
        }
        if (dayMoments.length > 0) {
          lines.push(`  ⚡  Momentos: ${dayMoments.map(m => m.label).join(", ")}`);
        }
        if (evening) {
          const mood     = EMOJIS[evening.mood] ?? "😐";
          const triggers = evening.triggers?.length ? ` · Triggers: ${evening.triggers.join(", ")}` : "";
          const consumo  = evening.consumo ? " · ⚠️ Consumo" : "";
          const notes    = evening.notes   ? `\n     "${evening.notes}"` : "";
          lines.push(`  🌙  Noche: ${mood} Riesgo ${evening.riskLevel}/10 · Ansiedad ${evening.ansiedad}/5${triggers}${consumo}${notes}`);
          totalRisk += evening.riskLevel ?? 0;
          riskCount++;
          evening.triggers?.forEach(t => { triggerCounts[t] = (triggerCounts[t] || 0) + 1; });
        }
      }
      lines.push("");
    }

    // Resumen final
    lines.push(SEP);
    lines.push(`RESUMEN (${numDays === 7 ? "semana" : "mes"})`);
    lines.push(`  Días registrados: ${regDays}/${numDays}`);
    if (riskCount > 0) lines.push(`  Riesgo medio: ${(totalRisk / riskCount).toFixed(1)}/10`);
    const top = Object.entries(triggerCounts).sort((a, b) => b[1] - a[1])[0];
    if (top) lines.push(`  Trigger más frecuente: ${top[0]} (${top[1]} ${top[1] === 1 ? "vez" : "veces"})`);
    lines.push("", `Exportado el ${new Date().toLocaleDateString("es-ES")} · reconectados.pages.dev`);

    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `reconectados-${label}-${dateKey()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const allEveningEntries = [...entries].reverse();

  return (
    <div className="page-container">
      {/* Header */}
      <div className="animate-fade-in-up flex items-start justify-between mb-5">
        <div>
          <h1 className="text-3xl font-bold text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>Diario</h1>
          <p className="text-sm text-[#2C4238] mt-1">Registro de tu recuperación</p>
        </div>
        <div className="flex items-center gap-2">
          {streak > 0 && (
            <div className="bg-[#EEF4F1] rounded-2xl px-3 py-2 text-center">
              <p className="text-lg font-bold text-[#6B9080]">{streak}</p>
              <p className="text-xs text-[#2C4238]">días</p>
            </div>
          )}
          <div className="flex gap-1">
            <button onClick={() => exportTxt(7)} className="flex items-center gap-1 px-2.5 h-9 rounded-xl bg-[#F7F9F7] border border-[#E4EBE6] text-xs font-semibold text-[#2C4238] hover:bg-[#EEF4F1] transition-colors" title="Exportar última semana">
              <Download size={13} className="text-[#6B9080]" />7d
            </button>
            <button onClick={() => exportTxt(30)} className="flex items-center gap-1 px-2.5 h-9 rounded-xl bg-[#F7F9F7] border border-[#E4EBE6] text-xs font-semibold text-[#2C4238] hover:bg-[#EEF4F1] transition-colors" title="Exportar último mes">
              <Download size={13} className="text-[#6B9080]" />30d
            </button>
          </div>
        </div>
      </div>

      {/* View tabs */}
      <div className="animate-fade-in-up delay-100 flex gap-1 bg-white rounded-2xl p-1 border border-[#E4EBE6] mb-5">
        {VIEWS.map(v => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${
              view === v.id
                ? "bg-[#6B9080] text-white shadow-sm"
                : "text-[#2C4238] hover:text-[#1E2F23]"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* ── TODAY VIEW ── */}
      {view === "today" && (
        <>
          {/* Morning check-in */}
          <div className="animate-fade-in-up delay-100">
            <MorningCheckIn onSave={reload} existing={morning} />
          </div>

          {/* Moment logger */}
          <div className="animate-fade-in-up delay-150">
            <MomentLogger onSave={reload} />
          </div>

          {/* Today's moments */}
          {todayMoments.length > 0 && (
            <div className="animate-fade-in-up delay-200 bg-white rounded-2xl border border-[#E4EBE6] p-4 mb-4">
              <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-3">Momentos de hoy</p>
              <div className="space-y-2">
                {todayMoments.map((m, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-lg">{m.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-[#1E2F23]">{m.label}</p>
                        <p className="text-xs text-[#4A6E5C]">{formatTime(m.time)}</p>
                      </div>
                      {m.note && <p className="text-xs text-[#2C4238] mt-0.5">{m.note}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Evening entry button */}
          {!showEvening && (
            <button
              onClick={() => setShowEvening(true)}
              className="animate-fade-in-up delay-200 w-full mb-4 py-4 rounded-2xl bg-[#6B9080] text-white font-semibold text-base flex items-center justify-center gap-2 hover:bg-[#5A7D6F] transition-colors"
            >
              <Moon size={18} />
              {hasEveningToday ? "Editar check-in nocturno" : "Nueva entrada nocturna"}
            </button>
          )}

          {showEvening && (
            <EveningForm onSave={() => { reload(); setShowEvening(false); }} onClose={() => setShowEvening(false)} />
          )}

          {/* Notification settings */}
          <div className="animate-fade-in-up delay-250">
            <NotifSettings />
          </div>

          {/* 14-day chart */}
          <div className="animate-fade-in-up delay-300">
            <RiskChart entries={allEveningEntries} />
          </div>
        </>
      )}

      {/* ── CALENDAR VIEW ── */}
      {view === "calendar" && (
        <>
          <div className="animate-fade-in-up delay-100">
            <JournalCalendar
              eveningEntries={allEveningEntries}
              morningMap={morningMap}
              momentsAll={momentsAll}
              onSelectDay={setSelectedDay}
            />
          </div>

          {/* Edit button for selected day */}
          {selectedDay && (
            <div className="flex items-center justify-between mb-4 px-1 animate-fade-in">
              <p className="text-xs text-[#2C4238] capitalize">
                {new Date(selectedDay + "T12:00:00").toLocaleDateString("es-ES", {
                  weekday: "long", day: "numeric", month: "long",
                })}
              </p>
              {selectedDay === isoDate(new Date()) ? (
                <button
                  onClick={() => setView("today")}
                  className="px-4 py-2 bg-[#EEF4F1] text-[#6B9080] text-xs font-semibold rounded-xl active:scale-95 transition-transform"
                >
                  Ir a Hoy →
                </button>
              ) : (
                <button
                  onClick={() => setEditOpen(true)}
                  className="px-4 py-2 bg-[#6B9080] text-white text-xs font-semibold rounded-xl active:scale-95 transition-transform"
                >
                  Editar día
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* ── PLAN VIEW ── */}
      {view === "plan" && (
        <div className="animate-fade-in-up delay-100">
          <RelapsePlan />
        </div>
      )}

      {/* Bottom-sheet modal editor — used from Calendar tab */}
      {editOpen && selectedDay && (
        <div
          className="fixed inset-0 z-50 bg-black/50"
          onClick={() => setEditOpen(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 max-h-[90vh] overflow-y-auto bg-[#F7F9F7] rounded-t-3xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-[#D1D9D5]" />
            </div>
            <div className="px-4 pb-10">
              <DayEditor
                key={selectedDay}
                dayKey={selectedDay}
                onClose={() => setEditOpen(false)}
                onSaved={() => { reload(); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── HISTORY VIEW ── */}
      {view === "history" && (
        <>
          {/* Weekly summary */}
          <div className="animate-fade-in-up delay-100">
            <WeeklySummary />
          </div>

          {/* 14-day chart */}
          <div className="animate-fade-in-up delay-150">
            <RiskChart entries={allEveningEntries} />
          </div>

          {entries.length === 0 && Object.keys(morningMap).length === 0 && momentsAll.length === 0 && (
            <div className="text-center py-12 animate-fade-in">
              <BookMarked size={48} className="text-[#4A6E5C] mx-auto mb-4" />
              <p className="text-[#2C4238] font-medium">Tu diario está vacío</p>
              <p className="text-sm text-[#4A6E5C] mt-1">Registrar tu día es una herramienta poderosa de autoconciencia</p>
            </div>
          )}

          {(entries.length > 0 || Object.keys(morningMap).length > 0 || momentsAll.length > 0) && (() => {
            // Build sorted set of all days with any data
            const allDays = new Set([
              ...allEveningEntries.map(e => e.date.slice(0, 10)),
              ...Object.keys(morningMap),
              ...momentsAll.map(m => m.time.slice(0, 10)),
            ]);
            const sortedDays = [...allDays].sort((a, b) => b.localeCompare(a));

            return (
              <div className="animate-fade-in-up delay-200">
                <p className="text-xs font-semibold text-[#2C4238] uppercase tracking-wide mb-3">Historial completo</p>
                <div className="space-y-3">
                  {sortedDays.map(day => {
                    const evening = allEveningEntries.find(e => e.date.slice(0, 10) === day);
                    const morn    = morningMap[day];
                    const dayMoms = momentsAll.filter(m => m.time.slice(0, 10) === day);
                    const dateLabel = new Date(day + "T12:00:00").toLocaleDateString("es-ES", {
                      weekday: "short", day: "numeric", month: "short",
                    });

                    return (
                      <div key={day} className="bg-white rounded-2xl p-4 border border-[#E4EBE6]">
                        <p className="text-sm font-semibold text-[#1E2F23] mb-2 capitalize">{dateLabel}</p>

                        {morn && (
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-5 h-5 rounded-md bg-[#FFF9EE] flex items-center justify-center flex-shrink-0">
                              <Sun size={11} className="text-[#F4A261]" />
                            </div>
                            <p className="text-xs text-[#2C4238]">
                              Mañana: {WAKE_STATES.find(w => w.id === morn.wakeState)?.label} · {morn.sueno}h sueño · Impulso {morn.impulso}/5
                              {morn.medicacion ? " · Medicación ✓" : ""}
                            </p>
                          </div>
                        )}

                        {dayMoms.length > 0 && (
                          <div className="flex items-start gap-2 mb-2">
                            <div className="w-5 h-5 rounded-md bg-[#EDF4FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Zap size={11} className="text-[#5B8FCC]" />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {dayMoms.map((m, i) => (
                                <span key={i} className="text-xs bg-[#F7F9F7] border border-[#E4EBE6] rounded-full px-2 py-0.5 text-[#2C4238]">
                                  {m.emoji} {m.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {evening && (
                          <div className="flex items-start gap-2">
                            <div className="w-5 h-5 rounded-md bg-[#EEF4F1] flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Moon size={11} className="text-[#6B9080]" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-base">{MOODS[evening.mood] ?? "😐"}</span>
                                <span className="text-xs font-medium" style={{ color: riskColor(evening.riskLevel) }}>
                                  Riesgo {evening.riskLevel}/10
                                </span>
                                {evening.ansiedad && (
                                  <span className="text-xs text-[#5B8FCC]">· Ansiedad {evening.ansiedad}/5</span>
                                )}
                                {evening.consumo && (
                                  <span className="text-xs text-[#E07A5F]">· Consumo</span>
                                )}
                              </div>
                              {evening.triggers?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {evening.triggers.map(t => (
                                    <span key={t} className="px-2 py-0.5 bg-[#F7F9F7] text-[#2C4238] rounded-full text-xs border border-[#E4EBE6]">{t}</span>
                                  ))}
                                </div>
                              )}
                              {evening.notes && (
                                <p className="text-xs text-[#2C4238] leading-relaxed mt-1 line-clamp-2">{evening.notes}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
